import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { authService } from '../services/authService';
import { tokenUtils } from '../utils/tokenUtils';
import { normalizeLocalUser } from '../utils/normalizeLocalUser';
import { AuthContext } from '../context/AuthContext';
import analyticsService from '../services/AnalyticsService';

const readStoredLocalUser = () => {
  try {
    const stored = localStorage.getItem('usuarioConCedula');
    return stored ? normalizeLocalUser(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
};

const persistLocalUser = (user) => {
  const normalized = normalizeLocalUser(user);
  if (normalized) {
    localStorage.setItem('usuarioConCedula', JSON.stringify(normalized));
  }
  return normalized;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [usuarioConCedula, setUsuarioConCedulaState] = useState(readStoredLocalUser);
  const [isLoadingUsuario, setIsLoadingUsuario] = useState(false);
  const [errorUsuario, setErrorUsuario] = useState(null);
  const userRef = useRef(user);
  const initialAuthChecked = useRef(false);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const setUsuarioConCedula = useCallback((value) => {
    if (!value) {
      setUsuarioConCedulaState(null);
      localStorage.removeItem('usuarioConCedula');
      return;
    }
    const normalized = persistLocalUser(value);
    setUsuarioConCedulaState(normalized);
  }, []);

  const refreshUsuarioConCedula = useCallback(async (cedulaArg) => {
    const cedulaToUse =
      cedulaArg ||
      userRef.current?.cedula ||
      userRef.current?.Cedula;

    if (!cedulaToUse) {
      setErrorUsuario('No hay cédula disponible para sincronizar el usuario local');
      return null;
    }

    setIsLoadingUsuario(true);
    setErrorUsuario(null);

    try {
      const token = tokenUtils.get();
      if (!token) {
        throw new Error('No hay token disponible');
      }

      const storedUser = tokenUtils.getUser() || userRef.current || { cedula: cedulaToUse };
      const data = await authService.ensureLocalUser(token, {
        ...storedUser,
        cedula: cedulaToUse,
      });

      const normalized = persistLocalUser(data);
      setUsuarioConCedulaState(normalized);
      return normalized;
    } catch (err) {
      setErrorUsuario(err.message);
      console.error('Error al obtener/crear usuarioConCedula:', err);
      throw err;
    } finally {
      setIsLoadingUsuario(false);
    }
  }, []);

  // Si ya hay sesión pero falta el perfil (label), re-sincronizar una vez
  useEffect(() => {
    const token = tokenUtils.get();
    const local = readStoredLocalUser();
    if (!token || !local?.id || !local?.cedula) return;
    if (local.profile || local.label?.name) return;

    let cancelled = false;
    (async () => {
      try {
        const refreshed = await authService.ensureLocalUser(token, local);
        if (!cancelled && refreshed) {
          const normalized = persistLocalUser(refreshed);
          setUsuarioConCedulaState(normalized);
        }
      } catch (e) {
        console.warn('No se pudo enriquecer el perfil del usuario:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    setErrorUsuario(null);
    try {
      const data = await authService.login(email, password);

      const storedToken = tokenUtils.get();
      if (!storedToken) {
        throw new Error('Error al guardar el token de autenticación');
      }

      const storedUser = tokenUtils.getUser();
      if (!storedUser) {
        throw new Error('Error al guardar los datos del usuario');
      }

      const localUser = normalizeLocalUser({
        ...data.localUser,
        email: data.localUser?.email || storedUser?.email,
        login: data.localUser?.login || data.localUser?.email || storedUser?.email,
      });
      if (!localUser?.id) {
        throw new Error('No se pudo registrar tu usuario en el sistema local.');
      }

      setUser(storedUser);
      setUsuarioConCedula(localUser);

      analyticsService.trackEvent('login_success', {
        userId: localUser.id,
        login: storedUser?.email || localUser.email || localUser.login,
        properties: { method: 'password' },
      });
      if (data?.isNewLocalUser) {
        analyticsService.trackEvent('registration', {
          userId: localUser.id,
          login: storedUser?.email || localUser.email || localUser.login,
          properties: { method: 'password' },
        });
      }
      analyticsService.startSessionTracking(localUser.id);

      return { ...data, localUser };
    } catch (err) {
      setError(err.message);
      setUser(null);
      setUsuarioConCedula(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setUsuarioConCedula]);

  const loginDoctorByCedula = useCallback(async (cedula, otp) => {
    setLoading(true);
    setError(null);
    setErrorUsuario(null);
    try {
      const data = await authService.loginDoctorByCedula(cedula, otp);

      const storedToken = tokenUtils.get();
      if (!storedToken) {
        throw new Error('Error al guardar el token de autenticación');
      }

      const storedUser = tokenUtils.getUser();
      if (!storedUser) {
        throw new Error('Error al guardar los datos del usuario');
      }

      const localUser = normalizeLocalUser({
        ...data.localUser,
        email: data.localUser?.email || storedUser?.email,
        login: data.localUser?.login || data.localUser?.email || storedUser?.email || storedUser?.cedula,
      });
      if (!localUser?.id) {
        throw new Error('No se pudo registrar tu usuario en el sistema local.');
      }

      setUser(storedUser);
      setUsuarioConCedula(localUser);

      analyticsService.trackEvent('login_success', {
        userId: localUser.id,
        login: storedUser?.email || localUser.email || localUser.login || storedUser?.cedula,
        properties: { method: 'habeas' },
      });
      if (data?.isNewLocalUser) {
        analyticsService.trackEvent('registration', {
          userId: localUser.id,
          login: storedUser?.email || localUser.email || localUser.login || storedUser?.cedula,
          properties: { method: 'habeas' },
        });
      }
      analyticsService.startSessionTracking(localUser.id);

      return { ...data, localUser };
    } catch (err) {
      setError(err.message);
      setUser(null);
      setUsuarioConCedula(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [setUsuarioConCedula]);

  const logout = useCallback(() => {
    const userId = usuarioConCedula?.id || readStoredLocalUser()?.id;
    analyticsService.stopSessionTracking(userId);
    window.isLoggingOut = true;
    authService.logout();
    setUser(null);
    setUsuarioConCedula(null);
    setErrorUsuario(null);
    setLoading(false);
  }, [setUsuarioConCedula, usuarioConCedula?.id]);

  const getAuthHeaders = useCallback(() => {
    return authService.getAuthHeaders();
  }, []);

  const checkAuthenticationStatus = useCallback(() => {
    const token = tokenUtils.get();
    const storedUser = tokenUtils.getUser();

    if (!token || !storedUser) {
      return false;
    }

    if (tokenUtils.isExpired(token)) {
      return false;
    }

    return true;
  }, []);

  // Solo al montar: evita re-ejecutar checkAuth tras login (eso borraba la sesión)
  useEffect(() => {
    if (initialAuthChecked.current) return;
    initialAuthChecked.current = true;

    const checkAuth = async () => {
      try {
        const token = tokenUtils.get();
        const storedUser = tokenUtils.getUser();

        if (!token || !storedUser) {
          setLoading(false);
          return;
        }

        if (tokenUtils.isExpired(token)) {
          console.log('Token expirado, limpiando sesión');
          logout();
          return;
        }

        // Usuarios doctor (habeas-data): solo usar datos del .NET API, no Laravel
        if (storedUser.authSource === 'habeas-data') {
          setUser(storedUser);
          if (storedUser?.cedula) {
            try {
              await refreshUsuarioConCedula(storedUser.cedula);
            } catch (syncErr) {
              console.error('No se pudo sincronizar usuario local (doctor):', syncErr);
              setErrorUsuario(syncErr.message);
              // Mantener lo que haya en localStorage si el sync falla
              const existing = readStoredLocalUser();
              if (existing) setUsuarioConCedulaState(existing);
            }
          }
          setLoading(false);
          return;
        }

        // Login convencional: verificar usuario en Laravel sin tumbar sesión local válida
        try {
          const userData = await authService.fetchUser();
          if (userData) {
            const mergedUser = {
              ...storedUser,
              ...userData,
              cedula: userData.cedula || storedUser.cedula,
            };
            tokenUtils.setUser(mergedUser);
            setUser(mergedUser);

            if (mergedUser?.cedula) {
              try {
                await refreshUsuarioConCedula(mergedUser.cedula);
              } catch (syncErr) {
                console.error('No se pudo sincronizar usuario local:', syncErr);
                setErrorUsuario(syncErr.message);
                const existing = readStoredLocalUser();
                if (existing) setUsuarioConCedulaState(existing);
              }
            }
          } else {
            // Laravel no respondió usuario, pero el token/.NET pueden seguir válidos
            setUser(storedUser);
            const existing = readStoredLocalUser();
            if (existing) {
              setUsuarioConCedulaState(existing);
            } else if (storedUser?.cedula) {
              try {
                await refreshUsuarioConCedula(storedUser.cedula);
              } catch (syncErr) {
                console.error('No se pudo sincronizar usuario local:', syncErr);
                setErrorUsuario(syncErr.message);
              }
            }
          }
        } catch (err) {
          console.error('Error al verificar usuario en servidor:', err);
          // Solo limpiar si es auth real Y no hay usuario local usable
          const existing = readStoredLocalUser();
          const isAuthError =
            err.message === 'No autorizado' || err.message === 'Sesión expirada';

          if (isAuthError && !existing?.id) {
            logout();
          } else {
            // Conservar sesión local (nombre en navbar / Mis cursos)
            setUser(storedUser);
            if (existing) setUsuarioConCedulaState(existing);
            else if (storedUser?.cedula) {
              try {
                await refreshUsuarioConCedula(storedUser.cedula);
              } catch (syncErr) {
                setErrorUsuario(syncErr.message);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error en checkAuth:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [logout, refreshUsuarioConCedula]);

  // Sesión analytics: heartbeat mientras haya usuario local
  useEffect(() => {
    const userId = usuarioConCedula?.id;
    if (!userId) return undefined;
    analyticsService.startSessionTracking(userId);
    return () => {
      analyticsService.stopSessionTracking(userId);
    };
  }, [usuarioConCedula?.id]);

  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      loading,
      error,
      login,
      loginDoctorByCedula,
      logout,
      getAuthHeaders,
      isAuthenticated: checkAuthenticationStatus(),
      usuarioConCedula,
      setUsuarioConCedula,
      isLoadingUsuario,
      errorUsuario,
      refreshUsuarioConCedula,
    }),
    [
      user,
      loading,
      error,
      login,
      loginDoctorByCedula,
      logout,
      getAuthHeaders,
      usuarioConCedula,
      setUsuarioConCedula,
      isLoadingUsuario,
      errorUsuario,
      refreshUsuarioConCedula,
      checkAuthenticationStatus,
    ]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
